import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Plus, FileText, Download, Eye, Trash2, DollarSign, Mail, Bell, Edit, Percent, Save, Tag, Search, Filter, Calendar, CreditCard } from 'lucide-react';
import { invoices as invoicesAPI, clients as clientsAPI, cashInjections as cashInjectionsAPI, notifications, savedItems as savedItemsAPI, paymentMethods as paymentMethodsAPI, productsServices } from '../api';
import { useToast } from '../hooks/use-toast';
import { formatMoney } from '../utils/currency';

const Invoices = ({ businessId, businessName, businessCurrency = 'USD', onUpdate }) => {
  const fmt = (amt) => formatMoney(amt, businessCurrency);
  const { toast } = useToast();
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cashInjections, setCashInjections] = useState([]);
  const [clients, setClients] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [productsCatalog, setProductsCatalog] = useState([]); // Products/Services from catalog
  const [filteredClients, setFilteredClients] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(null);
  const [showProductDropdown, setShowProductDropdown] = useState(null); // For product selection
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [isCashDialogOpen, setIsCashDialogOpen] = useState(false);
  const [isEditCashDialogOpen, setIsEditCashDialogOpen] = useState(false);
  const [editingCashInjection, setEditingCashInjection] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('issue_date'); // issue_date | created_at | amount | status
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_address: '',
    payment_info: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    status: 'pending',
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_end_date: ''
  });
  const [cashFormData, setCashFormData] = useState({
    amount: '',
    source: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [lineItems, setLineItems] = useState([
    { code: '', description: '', unit: 1, unit_price: 0, total: 0 }
  ]);
  const [taxRate, setTaxRate] = useState(0);
  
  // Discount state
  const [discountType, setDiscountType] = useState('none'); // 'none', 'percentage', 'fixed'
  const [discountValue, setDiscountValue] = useState(0);
  
  // Deposit state
  const [depositAmount, setDepositAmount] = useState(0);
  const [depositorName, setDepositorName] = useState('');

  // Filter invoices based on search and filters
  const filteredInvoices = invoices.filter(invoice => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      invoice.client_name?.toLowerCase().includes(searchLower) ||
      invoice.invoice_number?.toLowerCase().includes(searchLower) ||
      invoice.client_address?.toLowerCase().includes(searchLower);
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const invoiceDate = new Date(invoice.issue_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === 'today') {
        const invoiceDateOnly = new Date(invoiceDate);
        invoiceDateOnly.setHours(0, 0, 0, 0);
        matchesDate = invoiceDateOnly.getTime() === today.getTime();
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        matchesDate = invoiceDate >= weekAgo;
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        matchesDate = invoiceDate >= monthAgo;
      } else if (dateFilter === 'year') {
        const yearAgo = new Date(today);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        matchesDate = invoiceDate >= yearAgo;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  }).sort((a, b) => {
    if (sortBy === 'amount') return (b.amount || 0) - (a.amount || 0);
    if (sortBy === 'status') return (a.status || '').localeCompare(b.status || '');
    if (sortBy === 'created_at') return (b.created_at || '').localeCompare(a.created_at || '');
    // default: issue_date desc, with created_at tiebreaker
    const dateCmp = (b.issue_date || '').localeCompare(a.issue_date || '');
    if (dateCmp !== 0) return dateCmp;
    return (b.created_at || '').localeCompare(a.created_at || '');
  });

  // Parse invoice line items for view dialog
  const parseInvoiceItems = (invoice) => {
    try {
      if (invoice.line_items && invoice.line_items.length > 0) {
        return invoice.line_items;
      }
      if (invoice.description) {
        const parsed = JSON.parse(invoice.description);
        return parsed.items || [];
      }
    } catch (e) {
      // Not JSON
    }
    return [];
  };

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchCashInjections();
    fetchSavedItems();
    fetchPaymentMethods();
    fetchProductsCatalog();
  }, [businessId]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await paymentMethodsAPI.getAll(businessId);
      setPaymentMethods(response.data || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const fetchCashInjections = async () => {
    try {
      const response = await cashInjectionsAPI.getAll(businessId);
      setCashInjections(response.data);
    } catch (error) {
      console.error('Error fetching cash injections:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll(businessId);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchSavedItems = async () => {
    try {
      const response = await savedItemsAPI.getAll(businessId);
      setSavedItems(response.data);
    } catch (error) {
      console.error('Error fetching saved items:', error);
    }
  };

  const fetchProductsCatalog = async () => {
    try {
      const response = await productsServices.getAll(businessId);
      console.log('Products catalog loaded:', response.data);
      setProductsCatalog(response.data || []);
    } catch (error) {
      console.error('Error fetching products catalog:', error);
    }
  };

  const saveLineItem = async (item) => {
    if (!item.code) return;
    try {
      await savedItemsAPI.create({
        business_id: businessId,
        code: item.code,
        description: item.description,
        unit_price: item.unit_price
      });
      await fetchSavedItems();
      toast({
        title: 'Item Saved',
        description: `"${item.code}" saved for future use`,
      });
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoicesAPI.getAll(businessId);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoices',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals with discount
  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.unit * item.unit_price), 0);
    let discountAmount = 0;
    
    if (discountType === 'percentage' && discountValue > 0) {
      discountAmount = subtotal * (discountValue / 100);
    } else if (discountType === 'fixed' && discountValue > 0) {
      discountAmount = discountValue;
    }
    
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = afterDiscount * (taxRate / 100);
    const total = afterDiscount + taxAmount;
    
    return { subtotal, discountAmount, afterDiscount, taxAmount, total };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Filter clients when typing in client name
    if (name === 'client_name') {
      if (value.trim()) {
        const filtered = clients.filter(client => 
          client.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredClients(filtered);
        setShowClientDropdown(filtered.length > 0);
      } else {
        setShowClientDropdown(false);
      }
    }
  };

  const handleItemCodeChange = (index, value) => {
    const newItems = [...lineItems];
    newItems[index].code = value;
    setLineItems(newItems);
    
    // Build combined list of catalog products + previously-saved items, tagging with source.
    const catalogTagged = (productsCatalog || []).map(p => ({
      id: p.id,
      code: p.sku || p.name,
      description: p.description || p.name,
      unit_price: p.default_price,
      name: p.name,
      source: 'catalog',
    }));
    const savedTagged = (savedItems || []).map(s => ({ ...s, source: 'saved' }));
    const allItems = [...catalogTagged, ...savedTagged];
    
    if (value.trim()) {
      const v = value.toLowerCase();
      const filtered = allItems.filter(item =>
        (item.code || '').toLowerCase().includes(v) ||
        (item.description || '').toLowerCase().includes(v) ||
        (item.name || '').toLowerCase().includes(v)
      );
      setFilteredItems(filtered);
      setShowItemDropdown(filtered.length > 0 ? index : null);
    } else {
      // Empty input — show everything so the user can browse
      setFilteredItems(allItems);
      setShowItemDropdown(allItems.length > 0 ? index : null);
    }
  };

  const handleSelectSavedItem = (index, savedItem) => {
    const newItems = [...lineItems];
    // Handle both saved items and catalog products
    const itemCode = savedItem.source === 'catalog' ? (savedItem.code || savedItem.sku || savedItem.name) : savedItem.code;
    const itemDescription = savedItem.source === 'catalog' ? (savedItem.description || savedItem.name) : savedItem.description;
    const itemPrice = parseFloat(savedItem.unit_price || savedItem.default_price || 0);
    
    newItems[index] = {
      ...newItems[index],
      code: itemCode || '',
      description: itemDescription || '',
      unit_price: itemPrice,
      total: (newItems[index].unit || 1) * itemPrice
    };
    setLineItems(newItems);
    setShowItemDropdown(null);
  };

  const handleItemDescriptionChange = (index, value) => {
    const newItems = [...lineItems];
    newItems[index].description = value;
    setLineItems(newItems);

    // Filter dropdown by description text
    const catalogTagged = (productsCatalog || []).map(p => ({
      id: p.id,
      code: p.sku || p.name,
      description: p.description || p.name,
      unit_price: p.default_price,
      name: p.name,
      source: 'catalog',
    }));
    const savedTagged = (savedItems || []).map(s => ({ ...s, source: 'saved' }));
    const allItems = [...catalogTagged, ...savedTagged];

    if (value.trim()) {
      const v = value.toLowerCase();
      const filtered = allItems.filter(item =>
        (item.code || '').toLowerCase().includes(v) ||
        (item.description || '').toLowerCase().includes(v) ||
        (item.name || '').toLowerCase().includes(v)
      );
      setFilteredItems(filtered);
      setShowItemDropdown(filtered.length > 0 ? index : null);
    } else {
      setFilteredItems(allItems);
      setShowItemDropdown(allItems.length > 0 ? index : null);
    }
  };

  const openItemDropdownFromField = (index, e) => {
    const rect = e.target.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 2, left: rect.left });
    const catalogTagged = (productsCatalog || []).map(p => ({
      id: p.id,
      code: p.sku || p.name,
      description: p.description || p.name,
      unit_price: p.default_price,
      name: p.name,
      source: 'catalog',
    }));
    const savedTagged = (savedItems || []).map(s => ({ ...s, source: 'saved' }));
    const allItems = [...catalogTagged, ...savedTagged];
    setFilteredItems(allItems);
    if (allItems.length > 0) setShowItemDropdown(index);
  };

  const handleSelectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      client_name: client.name,
      client_address: client.address || ''
    }));
    setShowClientDropdown(false);
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...lineItems];
    updatedItems[index][field] = value;
    
    // Calculate total for this line item
    if (field === 'unit' || field === 'unit_price') {
      const unit = parseFloat(updatedItems[index].unit) || 0;
      const unitPrice = parseFloat(updatedItems[index].unit_price) || 0;
      updatedItems[index].total = unit * unitPrice;
    }
    
    setLineItems(updatedItems);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { code: '', description: '', unit: 1, unit_price: 0, total: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (taxRate / 100);
  };

  const calculateGrandTotal = () => {
    const totals = calculateTotals();
    return totals.total;
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    const totals = calculateTotals();
    
    try {
      // Create description from line items in structured format
      const itemsDescription = JSON.stringify({
        items: lineItems,
        subtotal: totals.subtotal,
        discount_type: discountType,
        discount_value: discountValue,
        discount_amount: totals.discountAmount,
        tax_rate: taxRate,
        tax_amount: totals.taxAmount,
        grand_total: totals.total
      });

      await invoicesAPI.create({
        business_id: businessId,
        ...formData,
        amount: totals.total,
        line_items: lineItems,
        discount_type: discountType,
        discount_value: discountValue,
        discount_amount: totals.discountAmount,
        deposit_amount: depositAmount,
        depositor_name: depositorName,
        tax_rate: taxRate,
        tax_amount: totals.taxAmount,
        description: itemsDescription
      });
      
      setIsDialogOpen(false);
      resetForm();
      await fetchInvoices();
      if (onUpdate) onUpdate();
      toast({
        title: 'Invoice Created',
        description: 'Invoice has been created successfully.',
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create invoice';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const handleDeleteInvoice = async (id) => {
    try {
      await invoicesAPI.delete(id);
      await fetchInvoices();
      if (onUpdate) onUpdate();
      toast({
        title: 'Invoice Deleted',
        description: 'Invoice has been deleted successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete invoice',
        variant: 'destructive'
      });
    }
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      client_name: invoice.client_name || '',
      client_address: invoice.client_address || '',
      payment_info: invoice.payment_info || '',
      issue_date: invoice.issue_date || '',
      due_date: invoice.due_date || '',
      status: invoice.status || 'pending',
      is_recurring: invoice.is_recurring || false,
      recurring_frequency: invoice.recurring_frequency || 'monthly',
      recurring_end_date: invoice.recurring_end_date || ''
    });
    
    // Preserve existing line items - parse from description if line_items not available
    let items = [];
    if (invoice.line_items && invoice.line_items.length > 0) {
      items = invoice.line_items.map(item => ({
        code: item.code || '',
        description: item.description || '',
        unit: item.unit || 1,
        unit_price: item.unit_price || 0,
        total: (item.unit || 1) * (item.unit_price || 0)
      }));
    } else if (invoice.description) {
      try {
        const parsed = JSON.parse(invoice.description);
        if (parsed.items && parsed.items.length > 0) {
          items = parsed.items.map(item => ({
            code: item.code || '',
            description: item.description || '',
            unit: item.unit || 1,
            unit_price: item.unit_price || 0,
            total: (item.unit || 1) * (item.unit_price || 0)
          }));
        }
      } catch (e) {
        // Not JSON, use default
      }
    }
    
    if (items.length === 0) {
      items = [{ code: '', description: '', unit: 1, unit_price: 0, total: 0 }];
    }
    
    setLineItems(items);
    setTaxRate(invoice.tax_rate || 0);
    setDiscountType(invoice.discount_type || 'none');
    setDiscountValue(invoice.discount_value || 0);
    setDepositAmount(invoice.deposit_amount || 0);
    setDepositorName(invoice.depositor_name || '');
    setIsEditDialogOpen(true);
  };

  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    
    if (!editingInvoice) return;
    
    const totals = calculateTotals();

    // Create description from line items in structured format (same as create)
    const itemsDescription = JSON.stringify({
      items: lineItems,
      subtotal: totals.subtotal,
      discount_type: discountType,
      discount_value: discountValue,
      discount_amount: totals.discountAmount,
      tax_rate: taxRate,
      tax_amount: totals.taxAmount,
      grand_total: totals.total
    });

    const invoiceData = {
      ...formData,
      line_items: lineItems,
      description: itemsDescription,
      subtotal: totals.subtotal,
      discount_type: discountType,
      discount_value: discountValue,
      discount_amount: totals.discountAmount,
      deposit_amount: depositAmount,
      depositor_name: depositorName,
      tax_rate: taxRate,
      tax_amount: totals.taxAmount,
      amount: totals.total
    };

    try {
      await invoicesAPI.update(editingInvoice.id, invoiceData);
      await fetchInvoices();
      if (onUpdate) onUpdate();
      setIsEditDialogOpen(false);
      setEditingInvoice(null);
      resetForm();
      toast({
        title: 'Invoice Updated',
        description: 'Invoice has been updated successfully.',
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update invoice';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_address: '',
      payment_info: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      status: 'pending',
      is_recurring: false,
      recurring_frequency: 'monthly',
      recurring_end_date: ''
    });
    setLineItems([{ code: '', description: '', unit: 1, unit_price: 0, total: 0 }]);
    setTaxRate(0);
    setDiscountType('none');
    setDiscountValue(0);
    setDepositAmount(0);
    setDepositorName('');
  };

  const handleExportInvoice = async (invoice, format) => {
    try {
      const response = await invoicesAPI.export(invoice.id, format);
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoice.invoice_number}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: 'Export Successful',
        description: `Invoice exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export invoice',
        variant: 'destructive'
      });
    }
  };

  const handleSendReminder = async (invoice) => {
    try {
      await invoicesAPI.sendReminder(invoice.id);
      toast({
        title: 'Reminder Sent',
        description: `Payment reminder sent for invoice ${invoice.invoice_number}`,
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send reminder';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const handleSendAllOverdueReminders = async () => {
    try {
      const response = await notifications.sendOverdueReminders(businessId);
      toast({
        title: 'Reminders Sent',
        description: `Sent ${response.data.sent_count} reminder(s). ${response.data.failed_count} failed.`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send overdue reminders',
        variant: 'destructive'
      });
    }
  };

  const handleCreateCashInjection = async (e) => {
    e.preventDefault();
    try {
      await cashInjectionsAPI.create({
        business_id: businessId,
        ...cashFormData,
        amount: parseFloat(cashFormData.amount)
      });
      setIsCashDialogOpen(false);
      setCashFormData({
        amount: '',
        source: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      await fetchCashInjections();
      if (onUpdate) onUpdate();
      toast({
        title: 'Cash Added',
        description: 'Cash injection recorded successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to record cash injection',
        variant: 'destructive'
      });
    }
  };

  const handleEditCashInjection = (injection) => {
    setEditingCashInjection(injection);
    setCashFormData({
      amount: injection.amount.toString(),
      source: injection.source,
      description: injection.description || '',
      date: injection.date
    });
    setIsEditCashDialogOpen(true);
  };

  const handleUpdateCashInjection = async (e) => {
    e.preventDefault();
    if (!editingCashInjection) return;
    
    try {
      await cashInjectionsAPI.update(editingCashInjection.id, {
        amount: parseFloat(cashFormData.amount),
        source: cashFormData.source,
        description: cashFormData.description,
        date: cashFormData.date
      });
      
      setIsEditCashDialogOpen(false);
      setEditingCashInjection(null);
      setCashFormData({
        amount: '',
        source: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      await fetchCashInjections();
      if (onUpdate) onUpdate();
      
      toast({
        title: 'Cash Injection Updated',
        description: 'Record has been updated successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update cash injection',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteCashInjection = async (id) => {
    try {
      await cashInjectionsAPI.delete(id);
      await fetchCashInjections();
      if (onUpdate) onUpdate();
      toast({
        title: 'Cash Injection Deleted',
        description: 'Record has been deleted successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete cash injection',
        variant: 'destructive'
      });
    }
  };

  const cashSourceOptions = [
    'Owner Investment',
    'Loan',
    'Contracted Income',
    'Interest Income',
    'Sale of Assets',
    'Refund',
    'Grant',
    'Other Income'
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-300';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading invoices...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-md">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-bold text-gray-800">Invoices - {businessName}</CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                <Plus className="h-4 w-4" />
                Create Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Invoice</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateInvoice} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <Label htmlFor="client_name">Client Name *</Label>
                    <Input
                      id="client_name"
                      name="client_name"
                      value={formData.client_name}
                      onChange={handleInputChange}
                      onFocus={() => {
                        if (formData.client_name && filteredClients.length > 0) {
                          setShowClientDropdown(true);
                        }
                      }}
                      required
                      placeholder="Start typing client name..."
                      autoComplete="off"
                    />
                    {showClientDropdown && filteredClients.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredClients.map((client) => (
                          <div
                            key={client.id}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => handleSelectClient(client)}
                          >
                            <div className="font-medium text-gray-900">{client.name}</div>
                            {client.address && (
                              <div className="text-sm text-gray-500 truncate">{client.address}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select name="status" value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="client_address">Client Address *</Label>
                  <Textarea
                    id="client_address"
                    name="client_address"
                    value={formData.client_address}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter client address"
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="payment_info">Payment Information</Label>
                  {paymentMethods.length > 0 && (
                    <div className="mb-2">
                      <Select onValueChange={(value) => {
                        const method = paymentMethods.find(m => m.id === value);
                        if (method) {
                          setFormData(prev => ({ ...prev, payment_info: `${method.name}\n${method.details}` }));
                        }
                      }}>
                        <SelectTrigger className="w-full">
                          <CreditCard className="h-4 w-4 mr-2" />
                          <SelectValue placeholder="Select a saved payment method..." />
                        </SelectTrigger>
                        <SelectContent>
                          {paymentMethods.map((method) => (
                            <SelectItem key={method.id} value={method.id}>
                              <span className="flex items-center gap-2">
                                {method.name} {method.is_default && '(Default)'}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Textarea
                    id="payment_info"
                    name="payment_info"
                    value={formData.payment_info}
                    onChange={handleInputChange}
                    placeholder="e.g., Bank Transfer: Account #12345, Wire Transfer, PayPal: business@example.com"
                    rows={3}
                  />
                  {paymentMethods.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Tip: Add payment methods in Settings to quickly select them here
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="issue_date">Issue Date *</Label>
                    <Input
                      id="issue_date"
                      name="issue_date"
                      type="date"
                      value={formData.issue_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="due_date">Due Date *</Label>
                    <Input
                      id="due_date"
                      name="due_date"
                      type="date"
                      value={formData.due_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Recurring Invoice Toggle */}
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4 text-blue-600" />
                      <Label className="font-semibold text-blue-800">Recurring Invoice</Label>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.is_recurring || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_recurring: e.target.checked }))}
                      className="h-5 w-5 rounded text-blue-600"
                      data-testid="recurring-toggle"
                    />
                  </div>
                  {formData.is_recurring && (
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <Label className="text-sm text-blue-700">Frequency</Label>
                        <Select
                          value={formData.recurring_frequency || 'monthly'}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, recurring_frequency: value }))}
                        >
                          <SelectTrigger data-testid="recurring-frequency">
                            <SelectValue placeholder="Frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="yearly">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-blue-700">End Date (optional)</Label>
                        <Input
                          type="date"
                          value={formData.recurring_end_date || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, recurring_end_date: e.target.value }))}
                          data-testid="recurring-end-date"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Line Items Table */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <Label className="text-base font-semibold">Line Items</Label>
                    <Button type="button" onClick={addLineItem} size="sm" className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-gray-300">
                          <th className="text-left py-2 px-2 text-sm font-semibold">Code</th>
                          <th className="text-left py-2 px-2 text-sm font-semibold">Description *</th>
                          <th className="text-left py-2 px-2 text-sm font-semibold">Unit *</th>
                          <th className="text-left py-2 px-2 text-sm font-semibold">Unit Price *</th>
                          <th className="text-right py-2 px-2 text-sm font-semibold">Total</th>
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((item, index) => (
                          <tr key={index} className="border-b border-gray-200">
                            <td className="py-2 px-2">
                              <Input
                                value={item.code}
                                onChange={(e) => handleItemCodeChange(index, e.target.value)}
                                onFocus={(e) => {
                                  const rect = e.target.getBoundingClientRect();
                                  setDropdownPos({ top: rect.bottom + 2, left: rect.left });
                                  const allItems = [
                                    ...savedItems.map(i => ({ ...i, source: 'saved' })),
                                    ...productsCatalog.map(p => ({ 
                                      id: p.id, 
                                      code: p.sku || p.name, 
                                      description: p.description || p.name, 
                                      unit_price: p.default_price,
                                      name: p.name,
                                      source: 'catalog' 
                                    }))
                                  ];
                                  setFilteredItems(allItems);
                                  setShowItemDropdown(index);
                                }}
                                onBlur={() => setTimeout(() => setShowItemDropdown(null), 300)}
                                placeholder="SKU / Product"
                                className="h-9"
                                autoComplete="off"
                                data-testid={`line-item-code-${index}`}
                              />
                              {showItemDropdown === index && filteredItems.length > 0 && ReactDOM.createPortal(
                                <div
                                  style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999, pointerEvents: 'auto' }}
                                  className="w-72 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto"
                                  data-testid="product-dropdown"
                                >
                                  {filteredItems.filter(i => i.source === 'catalog').length > 0 && (
                                    <div className="px-3 py-1 bg-blue-50 text-xs font-semibold text-blue-700 sticky top-0">
                                      Products & Services
                                    </div>
                                  )}
                                  {filteredItems.filter(i => i.source === 'catalog').map((catalogItem) => (
                                    <div
                                      key={`catalog-${catalogItem.id}`}
                                      className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                                      onMouseDown={(e) => { e.preventDefault(); handleSelectSavedItem(index, catalogItem); }}
                                      data-testid={`product-option-${catalogItem.id}`}
                                    >
                                      <div className="flex justify-between">
                                        <span className="font-medium text-sm">{catalogItem.name}</span>
                                        <span className="text-sm text-green-600">{formatMoney(catalogItem.unit_price || 0, businessCurrency)}</span>
                                      </div>
                                      <div className="text-xs text-gray-500">{catalogItem.code}</div>
                                      {catalogItem.description && (
                                        <div className="text-xs text-gray-400 truncate">{catalogItem.description}</div>
                                      )}
                                    </div>
                                  ))}
                                  {filteredItems.filter(i => i.source === 'saved').length > 0 && (
                                    <div className="px-3 py-1 bg-gray-50 text-xs font-semibold text-gray-600 sticky top-0">
                                      Previously Saved Items
                                    </div>
                                  )}
                                  {filteredItems.filter(i => i.source === 'saved').map((savedItem) => (
                                    <div
                                      key={`saved-${savedItem.id}`}
                                      className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                                      onMouseDown={(e) => { e.preventDefault(); handleSelectSavedItem(index, savedItem); }}
                                      data-testid={`saved-option-${savedItem.id}`}
                                    >
                                      <div className="flex justify-between">
                                        <span className="font-medium text-sm">{savedItem.code}</span>
                                        <span className="text-sm text-green-600">{formatMoney(savedItem.unit_price, businessCurrency)}</span>
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">{savedItem.description}</div>
                                    </div>
                                  ))}
                                </div>,
                                document.body
                              )}
                            </td>
                            <td className="py-2 px-2">
                              <Textarea
                                value={item.description}
                                onChange={(e) => handleItemDescriptionChange(index, e.target.value)}
                                onFocus={(e) => openItemDropdownFromField(index, e)}
                                onBlur={() => setTimeout(() => setShowItemDropdown(null), 300)}
                                placeholder="Item description (or pick from products)"
                                required
                                className="min-h-[36px] h-auto resize-none text-sm"
                                rows={1}
                                style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                                data-testid={`line-item-description-${index}`}
                              />
                            </td>
                            <td className="py-2 px-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit}
                                onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                                required
                                className="h-9"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                                required
                                className="h-9"
                              />
                            </td>
                            <td className="py-2 px-2 text-right font-semibold">
                              {formatMoney(item.total, businessCurrency)}
                            </td>
                            <td className="py-2 px-2">
                              <div className="flex gap-1">
                                {item.code && item.description && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => saveLineItem(item)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    title="Save for reuse"
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                )}
                                {lineItems.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeLineItem(index)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals Section */}
                  <div className="mt-4 border-t pt-4">
                    <div className="flex justify-end space-y-2">
                      <div className="w-96">
                        <div className="flex justify-between py-2 text-sm">
                          <span className="font-medium">Subtotal:</span>
                          <span className="font-semibold">{formatMoney(calculateTotals().subtotal, businessCurrency)}</span>
                        </div>
                        
                        {/* Discount Section */}
                        <div className="flex justify-between items-center py-2 text-sm border-t border-dashed">
                          <div className="flex items-center gap-2">
                            <Percent className="h-4 w-4 text-green-600" />
                            <span className="font-medium">Discount:</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select value={discountType} onValueChange={setDiscountType}>
                              <SelectTrigger className="w-28 h-8" data-testid="discount-type-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="percentage">Percent (%)</SelectItem>
                                <SelectItem value="fixed">Fixed ($)</SelectItem>
                              </SelectContent>
                            </Select>
                            {discountType !== 'none' && (
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={discountValue}
                                onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                                className="w-20 h-8 text-right"
                                placeholder={discountType === 'percentage' ? '%' : '$'}
                                data-testid="discount-value-input"
                              />
                            )}
                          </div>
                        </div>
                        {discountType !== 'none' && calculateTotals().discountAmount > 0 && (
                          <div className="flex justify-between py-1 text-sm text-green-600">
                            <span>Discount Amount:</span>
                            <span>-{formatMoney(calculateTotals().discountAmount, businessCurrency)}</span>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center py-2 text-sm">
                          <span className="font-medium">Tax Rate (%):</span>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={taxRate}
                            onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                            className="w-24 h-8 text-right"
                          />
                        </div>
                        <div className="flex justify-between py-2 text-sm">
                          <span className="font-medium">Tax Amount:</span>
                          <span className="font-semibold">{formatMoney(calculateTotals().taxAmount, businessCurrency)}</span>
                        </div>
                        <div className="flex justify-between py-3 text-lg border-t-2 border-gray-300">
                          <span className="font-bold">Grand Total:</span>
                          <span className="font-bold text-blue-600">{formatMoney(calculateTotals().total, businessCurrency)}</span>
                        </div>
                        
                        {/* Deposit Section */}
                        <div className="mt-4 pt-4 border-t border-dashed">
                          <div className="flex justify-between items-center py-2 text-sm">
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-green-600" />
                              <span className="font-medium">Deposit Received:</span>
                            </div>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={depositAmount}
                              onChange={(e) => setDepositAmount(parseFloat(e.target.value) || 0)}
                              className="w-28 h-8 text-right"
                              placeholder="$0.00"
                              data-testid="deposit-amount-input"
                            />
                          </div>
                          {depositAmount > 0 && (
                            <div className="flex justify-between items-center py-2 text-sm">
                              <span className="font-medium text-gray-600">Depositor Name:</span>
                              <Input
                                type="text"
                                value={depositorName}
                                onChange={(e) => setDepositorName(e.target.value)}
                                className="w-40 h-8"
                                placeholder="Name"
                                data-testid="depositor-name-input"
                              />
                            </div>
                          )}
                          {depositAmount > 0 && (
                            <div className="flex justify-between py-3 text-lg border-t-2 border-red-300 mt-2">
                              <span className="font-bold text-red-600">Balance Due:</span>
                              <span className="font-bold text-red-600">{formatMoney((calculateTotals().total - depositAmount), businessCurrency)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Create Invoice
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Bar */}
          <div className="mb-6 space-y-4">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by client, invoice #..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="invoice-search"
                />
              </div>
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="invoice-status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Date Filter */}
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-[150px]" data-testid="invoice-date-filter">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort By */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]" data-testid="invoice-sort-by">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="issue_date">Newest Issue Date</SelectItem>
                  <SelectItem value="created_at">Newest Created</SelectItem>
                  <SelectItem value="amount">Highest Amount</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Results count */}
            {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
              <p className="text-sm text-gray-500">
                Showing {filteredInvoices.length} of {invoices.length} invoices
                {searchTerm && <span> matching "{searchTerm}"</span>}
              </p>
            )}
          </div>

          {invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No invoices yet</p>
              <p className="text-gray-400 text-sm">Create your first invoice to get started</p>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No invoices found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Issue Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {invoice.invoice_number}
                        {invoice.is_recurring && invoice.recurring_status === 'active' && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700" data-testid={`recurring-badge-${invoice.id}`}>
                            <Bell className="h-3 w-3 mr-1" />
                            {invoice.recurring_frequency}
                          </span>
                        )}
                        {invoice.parent_recurring_id && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Auto
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{invoice.client_name}</td>
                      <td className="px-4 py-4 text-sm font-semibold text-gray-900">{formatMoney(invoice.amount, businessCurrency)}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{invoice.issue_date}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{invoice.due_date}</td>
                      <td className="px-4 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(invoice.status)}`}>
                          {invoice.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewInvoice(invoice)}
                            className="hover:bg-blue-50"
                            title="View Invoice"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditInvoice(invoice)}
                            className="hover:bg-purple-50 text-purple-600"
                            title="Edit Invoice"
                            data-testid={`edit-invoice-${invoice.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendReminder(invoice)}
                            className="hover:bg-yellow-50 text-yellow-600"
                            title="Send Payment Reminder"
                            data-testid={`send-reminder-${invoice.id}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExportInvoice(invoice, 'pdf')}
                            className="hover:bg-green-50"
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            className="hover:bg-red-50 text-red-600"
                            title="Delete Invoice"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send All Overdue Reminders Button */}
      {invoices.some(inv => inv.status === 'overdue') && (
        <Card className="shadow-md border-t-4 border-t-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="h-6 w-6 text-red-500" />
                <div>
                  <p className="font-semibold text-gray-800">Overdue Invoices</p>
                  <p className="text-sm text-gray-600">
                    {invoices.filter(inv => inv.status === 'overdue').length} invoice(s) are overdue
                  </p>
                </div>
              </div>
              <Button
                onClick={handleSendAllOverdueReminders}
                className="bg-red-600 hover:bg-red-700 gap-2"
                data-testid="send-all-overdue-reminders"
              >
                <Mail className="h-4 w-4" />
                Send All Overdue Reminders
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cash Injection Section */}
      <Card className="shadow-md border-t-4 border-t-green-500">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold text-gray-800">Cash Injections - {businessName}</CardTitle>
            <p className="text-sm text-gray-600 mt-1">Add cash to cash flow without creating an invoice</p>
          </div>
          <Dialog open={isCashDialogOpen} onOpenChange={setIsCashDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700 gap-2">
                <DollarSign className="h-4 w-4" />
                Add Cash
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Cash Injection</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCashInjection} className="space-y-4">
                <div>
                  <Label htmlFor="cash-amount">Amount ($) *</Label>
                  <Input
                    id="cash-amount"
                    type="number"
                    step="0.01"
                    value={cashFormData.amount}
                    onChange={(e) => setCashFormData({...cashFormData, amount: e.target.value})}
                    required
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="source">Source *</Label>
                  <Select value={cashFormData.source} onValueChange={(value) => setCashFormData({...cashFormData, source: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {cashSourceOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="cash-date">Date *</Label>
                  <Input
                    id="cash-date"
                    type="date"
                    value={cashFormData.date}
                    onChange={(e) => setCashFormData({...cashFormData, date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="cash-description">Description</Label>
                  <Textarea
                    id="cash-description"
                    value={cashFormData.description}
                    onChange={(e) => setCashFormData({...cashFormData, description: e.target.value})}
                    placeholder="Enter details about this cash injection"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsCashDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    Add Cash
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {cashInjections.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No cash injections recorded</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {cashInjections.map((injection) => (
                    <tr key={injection.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm text-gray-600">{injection.date}</td>
                      <td className="px-4 py-4"><span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">{injection.source}</span></td>
                      <td className="px-4 py-4 text-sm text-gray-700">{injection.description || '-'}</td>
                      <td className="px-4 py-4 text-sm font-semibold text-green-600">{formatMoney(injection.amount, businessCurrency)}</td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditCashInjection(injection)}
                            className="hover:bg-blue-50 text-blue-600"
                            data-testid={`edit-cash-${injection.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteCashInjection(injection.id)}
                            className="hover:bg-red-50 text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Cash Injection Dialog */}
      <Dialog open={isEditCashDialogOpen} onOpenChange={(open) => { setIsEditCashDialogOpen(open); if (!open) setEditingCashInjection(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Cash Injection</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateCashInjection} className="space-y-4">
            <div>
              <Label htmlFor="edit_cash_source">Source *</Label>
              <Select value={cashFormData.source} onValueChange={(v) => setCashFormData({ ...cashFormData, source: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {cashSourceOptions.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_cash_amount">Amount *</Label>
              <Input
                id="edit_cash_amount"
                type="number"
                step="0.01"
                value={cashFormData.amount}
                onChange={(e) => setCashFormData({ ...cashFormData, amount: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_cash_date">Date *</Label>
              <Input
                id="edit_cash_date"
                type="date"
                value={cashFormData.date}
                onChange={(e) => setCashFormData({ ...cashFormData, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_cash_description">Description</Label>
              <Textarea
                id="edit_cash_description"
                value={cashFormData.description}
                onChange={(e) => setCashFormData({ ...cashFormData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setIsEditCashDialogOpen(false); setEditingCashInjection(null); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                Update
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {viewInvoice && (
        <Dialog open={!!viewInvoice} onOpenChange={() => setViewInvoice(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Invoice Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-red-50 p-6 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{businessName}</h3>
                    <p className="text-gray-600">Invoice #{viewInvoice.invoice_number}</p>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(viewInvoice.status)}`}>
                    {viewInvoice.status.toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 font-medium">Client:</p>
                    <p className="text-gray-900 font-semibold">{viewInvoice.client_name}</p>
                    {viewInvoice.client_address && (
                      <p className="text-gray-600 text-xs mt-1">{viewInvoice.client_address}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Total Amount:</p>
                    <p className="text-2xl font-bold text-blue-600">{formatMoney(viewInvoice.amount || 0, businessCurrency)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Issue Date:</p>
                    <p className="text-gray-900">{viewInvoice.issue_date}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Due Date:</p>
                    <p className="text-gray-900">{viewInvoice.due_date}</p>
                  </div>
                </div>
              </div>
              
              {/* Line Items Table */}
              {parseInvoiceItems(viewInvoice).length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-700">Line Items</div>
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Code</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Description</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Qty</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Unit Price</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {parseInvoiceItems(viewInvoice).map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm text-gray-700">{item.code || '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.description}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{item.unit}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatMoney(parseFloat(item.unit_price), businessCurrency)}</td>
                          <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right">{formatMoney(parseFloat(item.total), businessCurrency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Totals Section */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="space-y-2 text-sm">
                  {viewInvoice.subtotal && (
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>{formatMoney(parseFloat(viewInvoice.subtotal), businessCurrency)}</span>
                    </div>
                  )}
                  {viewInvoice.discount_amount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({viewInvoice.discount_type === 'percentage' ? `${viewInvoice.discount_value}%` : 'Fixed'}):</span>
                      <span>-{formatMoney(parseFloat(viewInvoice.discount_amount), businessCurrency)}</span>
                    </div>
                  )}
                  {viewInvoice.tax_rate > 0 && (
                    <div className="flex justify-between">
                      <span>Tax ({viewInvoice.tax_rate}%):</span>
                      <span>{formatMoney(parseFloat(viewInvoice.tax_amount || 0), businessCurrency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Grand Total:</span>
                    <span className="text-blue-600">{formatMoney(viewInvoice.amount || 0, businessCurrency)}</span>
                  </div>
                  {viewInvoice.deposit_amount > 0 && (
                    <>
                      <div className="flex justify-between text-green-600 border-t pt-2">
                        <span>Deposit Received{viewInvoice.depositor_name ? ` from ${viewInvoice.depositor_name}` : ''}:</span>
                        <span>-{formatMoney(parseFloat(viewInvoice.deposit_amount), businessCurrency)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg text-red-600">
                        <span>Balance Due:</span>
                        <span>{formatMoney((viewInvoice.amount - viewInvoice.deposit_amount), businessCurrency)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Payment Info */}
              {viewInvoice.payment_info && (
                <div className="border rounded-lg p-4">
                  <p className="text-gray-600 font-medium mb-2">Payment Information:</p>
                  <p className="text-gray-900 whitespace-pre-wrap">{viewInvoice.payment_info}</p>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    handleEditInvoice(viewInvoice);
                    setViewInvoice(null);
                  }}
                  className="gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Edit Invoice
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExportInvoice(viewInvoice, 'pdf')}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExportInvoice(viewInvoice, 'excel')}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Invoice Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) {
          setEditingInvoice(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Invoice {editingInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateInvoice} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_client_name">Client Name</Label>
                <Input
                  id="edit_client_name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger id="edit_status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="edit_client_address">Client Address</Label>
              <Textarea
                id="edit_client_address"
                value={formData.client_address}
                onChange={(e) => setFormData({...formData, client_address: e.target.value})}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_issue_date">Issue Date</Label>
                <Input
                  id="edit_issue_date"
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_due_date">Due Date</Label>
                <Input
                  id="edit_due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  required
                />
              </div>
            </div>

            {/* Line Items */}
            <div>
              <Label className="text-base font-semibold">Line Items</Label>
              <div className="mt-2 space-y-2">
                {lineItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-6 gap-2 items-start">
                    <div>
                      <Input
                        placeholder="Code"
                        value={item.code}
                        onChange={(e) => {
                          const newItems = [...lineItems];
                          newItems[index].code = e.target.value;
                          setLineItems(newItems);
                        }}
                        onFocus={(e) => {
                          const rect = e.target.getBoundingClientRect();
                          setDropdownPos({ top: rect.bottom + 2, left: rect.left });
                          const allItems = [
                            ...savedItems.map(i => ({ ...i, source: 'saved' })),
                            ...productsCatalog.map(p => ({ 
                              id: p.id, 
                              code: p.sku || p.name, 
                              description: p.description || p.name, 
                              unit_price: p.default_price,
                              name: p.name,
                              source: 'catalog' 
                            }))
                          ];
                          setFilteredItems(allItems);
                          setShowItemDropdown(index);
                        }}
                        onBlur={() => setTimeout(() => setShowItemDropdown(null), 300)}
                        autoComplete="off"
                        data-testid={`edit-line-item-code-${index}`}
                      />
                      {showItemDropdown === index && filteredItems.length > 0 && ReactDOM.createPortal(
                        <div
                          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999, pointerEvents: 'auto' }}
                          className="w-72 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto"
                          data-testid="edit-product-dropdown"
                        >
                          {filteredItems.filter(i => i.source === 'catalog').length > 0 && (
                            <div className="px-3 py-1 bg-blue-50 text-xs font-semibold text-blue-700 sticky top-0">
                              Products & Services
                            </div>
                          )}
                          {filteredItems.filter(i => i.source === 'catalog').map((catalogItem) => (
                            <div
                              key={`catalog-${catalogItem.id}`}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const newItems = [...lineItems];
                                newItems[index] = {
                                  ...newItems[index],
                                  code: catalogItem.code,
                                  description: catalogItem.description,
                                  unit_price: parseFloat(catalogItem.unit_price || 0),
                                  total: newItems[index].unit * parseFloat(catalogItem.unit_price || 0)
                                };
                                setLineItems(newItems);
                                setShowItemDropdown(null);
                              }}
                              data-testid={`edit-product-option-${catalogItem.id}`}
                            >
                              <div className="flex justify-between">
                                <span className="font-medium text-sm">{catalogItem.name}</span>
                                <span className="text-sm text-green-600">{formatMoney(catalogItem.unit_price || 0, businessCurrency)}</span>
                              </div>
                              {catalogItem.description && (
                                <div className="text-xs text-gray-400 truncate">{catalogItem.description}</div>
                              )}
                            </div>
                          ))}
                          {filteredItems.filter(i => i.source === 'saved').length > 0 && (
                            <div className="px-3 py-1 bg-gray-50 text-xs font-semibold text-gray-600 sticky top-0">
                              Previously Saved Items
                            </div>
                          )}
                          {filteredItems.filter(i => i.source === 'saved').map((savedItem) => (
                            <div
                              key={`saved-${savedItem.id}`}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const newItems = [...lineItems];
                                newItems[index] = {
                                  ...newItems[index],
                                  code: savedItem.code,
                                  description: savedItem.description,
                                  unit_price: parseFloat(savedItem.unit_price || 0),
                                  total: newItems[index].unit * parseFloat(savedItem.unit_price || 0)
                                };
                                setLineItems(newItems);
                                setShowItemDropdown(null);
                              }}
                              data-testid={`edit-saved-option-${savedItem.id}`}
                            >
                              <div className="flex justify-between">
                                <span className="font-medium text-sm">{savedItem.code}</span>
                                <span className="text-sm text-green-600">{formatMoney(savedItem.unit_price, businessCurrency)}</span>
                              </div>
                              <div className="text-xs text-gray-500 truncate">{savedItem.description}</div>
                            </div>
                          ))}
                        </div>,
                        document.body
                      )}
                    </div>
                    <Textarea
                      placeholder="Description"
                      className="col-span-2 min-h-[40px] h-auto resize-none text-sm"
                      rows={1}
                      value={item.description}
                      onChange={(e) => {
                        const newItems = [...lineItems];
                        newItems[index].description = e.target.value;
                        setLineItems(newItems);
                      }}
                      style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.unit}
                      onChange={(e) => {
                        const newItems = [...lineItems];
                        newItems[index].unit = parseFloat(e.target.value) || 0;
                        newItems[index].total = newItems[index].unit * newItems[index].unit_price;
                        setLineItems(newItems);
                      }}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Unit Price"
                      value={item.unit_price}
                      onChange={(e) => {
                        const newItems = [...lineItems];
                        newItems[index].unit_price = parseFloat(e.target.value) || 0;
                        newItems[index].total = newItems[index].unit * newItems[index].unit_price;
                        setLineItems(newItems);
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{formatMoney((item.unit * item.unit_price), businessCurrency)}</span>
                      {lineItems.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setLineItems(lineItems.filter((_, i) => i !== index))}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setLineItems([...lineItems, { code: '', description: '', unit: 1, unit_price: 0, total: 0 }])}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Line Item
                </Button>
              </div>
            </div>

            {/* Tax, Discount, and Total */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Subtotal:</span>
                  <span>{formatMoney(calculateTotals().subtotal, businessCurrency)}</span>
                </div>
                
                {/* Discount */}
                <div className="flex justify-between items-center text-sm border-t border-dashed pt-2">
                  <div className="flex items-center gap-2">
                    <Percent className="h-4 w-4 text-green-600" />
                    <span className="font-medium">Discount:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={discountType} onValueChange={setDiscountType}>
                      <SelectTrigger className="w-28 h-8" data-testid="edit-discount-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="percentage">Percent (%)</SelectItem>
                        <SelectItem value="fixed">Fixed ($)</SelectItem>
                      </SelectContent>
                    </Select>
                    {discountType !== 'none' && (
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                        className="w-20 h-8 text-right"
                        data-testid="edit-discount-value"
                      />
                    )}
                  </div>
                </div>
                {discountType !== 'none' && calculateTotals().discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount Amount:</span>
                    <span>-{formatMoney(calculateTotals().discountAmount, businessCurrency)}</span>
                  </div>
                )}
                
                {/* Tax */}
                <div className="flex justify-between items-center text-sm">
                  <span className="font-medium">Tax Rate (%):</span>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={taxRate}
                    onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                    className="w-24 h-8 text-right"
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Tax Amount:</span>
                  <span>{formatMoney(calculateTotals().taxAmount, businessCurrency)}</span>
                </div>
                
                {/* Grand Total */}
                <div className="flex justify-between text-lg font-bold border-t-2 pt-3">
                  <span>Grand Total:</span>
                  <span className="text-blue-600">{formatMoney(calculateTotals().total, businessCurrency)}</span>
                </div>
                
                {/* Deposit Section */}
                <div className="mt-3 pt-3 border-t border-dashed">
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      <span className="font-medium">Deposit Received:</span>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(parseFloat(e.target.value) || 0)}
                      className="w-28 h-8 text-right"
                      placeholder="$0.00"
                    />
                  </div>
                  {depositAmount > 0 && (
                    <div className="flex justify-between items-center text-sm mt-2">
                      <span className="font-medium text-gray-600">Depositor Name:</span>
                      <Input
                        type="text"
                        value={depositorName}
                        onChange={(e) => setDepositorName(e.target.value)}
                        className="w-40 h-8"
                        placeholder="Name"
                      />
                    </div>
                  )}
                  {depositAmount > 0 && (
                    <div className="flex justify-between text-lg font-bold border-t-2 border-red-300 pt-3 mt-3">
                      <span className="text-red-600">Balance Due:</span>
                      <span className="text-red-600">{formatMoney((calculateTotals().total - depositAmount), businessCurrency)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div>
              <Label htmlFor="edit_payment_info">Payment Information</Label>
              <Textarea
                id="edit_payment_info"
                value={formData.payment_info}
                onChange={(e) => setFormData({...formData, payment_info: e.target.value})}
                placeholder="Bank details, PayPal, etc."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setIsEditDialogOpen(false);
                setEditingInvoice(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Update Invoice
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
